import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Bell, Mail, MessageSquare, Shield, Send, Clock, AlertTriangle, CheckCircle2, Info, Settings, Sparkles, Zap, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_COLORS = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  info: 'bg-blue-100 text-blue-800 border-blue-200'
};

const SEVERITY_ICONS = {
  critical: <AlertTriangle className="h-4 w-4 text-red-600" />,
  high: <AlertTriangle className="h-4 w-4 text-orange-600" />,
  warning: <Info className="h-4 w-4 text-yellow-600" />,
  info: <Info className="h-4 w-4 text-blue-600" />
};

const CATEGORY_LABELS = {
  inventory: 'Inventory',
  sales: 'Sales',
  operations: 'Operations',
  quality: 'Quality',
  procurement: 'Procurement',
  finance: 'Finance',
  seasonal: 'Seasonal',
  system: 'System',
  forecasting: 'Forecasting'
};

const AlertSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertTypes, setAlertTypes] = useState({});
  const [config, setConfig] = useState(null);
  const [alertHistory, setAlertHistory] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [recsLoading, setRecsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('channels');

  const tenantId = user?.tenantId || 'TENANT-001';
  const isAdmin = ['superadmin', 'admin', 'company_admin'].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [typesRes, configRes, historyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/alerts/types`, { headers }),
        axios.get(`${BACKEND_URL}/api/alerts/config/${tenantId}`, { headers }),
        axios.get(`${BACKEND_URL}/api/alerts/history/${tenantId}?limit=20`, { headers })
      ]);

      setAlertTypes(typesRes.data?.alert_types || {});
      setConfig(configRes.data || {});
      setAlertHistory(historyRes.data?.alerts || []);
      fetchRecommendations(token);
    } catch (err) {
      console.error('Failed to fetch alert data:', err);
      toast.error('Failed to load alert configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async (token) => {
    setRecsLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/alerts/recommendations/${tenantId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRecommendations(res.data?.recommendations || []);
    } catch (err) {
      console.error('Failed to fetch recommendations:', err);
    } finally {
      setRecsLoading(false);
    }
  };

  const applyRecommendation = (rec) => {
    setConfig(prev => ({
      ...prev,
      alert_rules: {
        ...prev.alert_rules,
        [rec.alert_type]: {
          ...prev.alert_rules?.[rec.alert_type],
          enabled: true,
          threshold: rec.suggested_threshold
        }
      }
    }));
    toast.success(`Applied ${rec.name} threshold: ${rec.suggested_threshold}${rec.unit}`);
  };

  const applyAllRecommendations = () => {
    const updated = { ...config };
    recommendations.forEach(rec => {
      updated.alert_rules = {
        ...updated.alert_rules,
        [rec.alert_type]: {
          ...updated.alert_rules?.[rec.alert_type],
          enabled: true,
          threshold: rec.suggested_threshold
        }
      };
    });
    setConfig(updated);
    toast.success(`Applied all ${recommendations.length} recommendations`);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/alerts/config/${tenantId}`, config, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success('Alert configuration saved successfully');
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestAlert = async (channel) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/alerts/test/${tenantId}?channel=${channel}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`Test ${channel} alert sent!`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to send test ${channel} alert`);
    }
  };

  const updateEmailConfig = (field, value) => {
    setConfig(prev => ({
      ...prev,
      email: { ...prev.email, [field]: value }
    }));
  };

  const updateWhatsAppConfig = (field, value) => {
    setConfig(prev => ({
      ...prev,
      whatsapp: { ...prev.whatsapp, [field]: value }
    }));
  };

  const toggleAlertRule = (alertId, field, value) => {
    setConfig(prev => ({
      ...prev,
      alert_rules: {
        ...prev.alert_rules,
        [alertId]: {
          ...prev.alert_rules?.[alertId],
          [field]: value
        }
      }
    }));
  };

  const updateSchedule = (field, value) => {
    setConfig(prev => ({
      ...prev,
      schedule: { ...prev.schedule, [field]: value }
    }));
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto text-slate-400 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
            <p className="text-slate-500">Only tenant admins and super admins can manage alert configurations.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const groupedAlerts = Object.entries(alertTypes).reduce((acc, [id, info]) => {
    const cat = info.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push({ id, ...info });
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto" data-testid="alert-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Bell className="h-6 w-6 text-blue-600" />
            Alert & Notification Settings
          </h1>
          <p className="text-sm text-slate-500 mt-1">Configure email and WhatsApp Business alerts for your retail operations</p>
        </div>
        <Button onClick={handleSaveConfig} disabled={saving} data-testid="save-alert-config-btn">
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="channels" data-testid="tab-channels">
            <Settings className="h-4 w-4 mr-1" /> Channels
          </TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">
            <Bell className="h-4 w-4 mr-1" /> Alert Rules
          </TabsTrigger>
          <TabsTrigger value="smart" data-testid="tab-smart">
            <Sparkles className="h-4 w-4 mr-1" /> Smart
          </TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">
            <Clock className="h-4 w-4 mr-1" /> Schedule
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <AlertTriangle className="h-4 w-4 mr-1" /> History
          </TabsTrigger>
        </TabsList>

        {/* ============= CHANNELS TAB ============= */}
        <TabsContent value="channels" className="space-y-6 mt-4">
          {/* Email Configuration */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <div>
                    <CardTitle>Email Notifications</CardTitle>
                    <CardDescription>Configure SMTP email delivery for alerts</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={config?.email?.enabled || false}
                    onCheckedChange={(checked) => updateEmailConfig('enabled', checked)}
                    data-testid="email-enabled-switch"
                  />
                  <span className="text-sm">{config?.email?.enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
            </CardHeader>
            {config?.email?.enabled && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>SMTP Host</Label>
                    <Input
                      placeholder="smtp.gmail.com"
                      value={config?.email?.smtp_host || ''}
                      onChange={(e) => updateEmailConfig('smtp_host', e.target.value)}
                      data-testid="smtp-host-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Port</Label>
                    <Input
                      type="number"
                      placeholder="587"
                      value={config?.email?.smtp_port || 587}
                      onChange={(e) => updateEmailConfig('smtp_port', parseInt(e.target.value))}
                      data-testid="smtp-port-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Username</Label>
                    <Input
                      placeholder="alerts@company.com"
                      value={config?.email?.smtp_user || ''}
                      onChange={(e) => updateEmailConfig('smtp_user', e.target.value)}
                      data-testid="smtp-user-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Password</Label>
                    <Input
                      type="password"
                      placeholder="App password"
                      value={config?.email?.smtp_password || ''}
                      onChange={(e) => updateEmailConfig('smtp_password', e.target.value)}
                      data-testid="smtp-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>From Email</Label>
                    <Input
                      placeholder="alerts@reasonretailiq.com"
                      value={config?.email?.from_email || ''}
                      onChange={(e) => updateEmailConfig('from_email', e.target.value)}
                      data-testid="email-from-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Recipients (comma-separated)</Label>
                    <Input
                      placeholder="admin@company.com, ops@company.com"
                      value={(config?.email?.recipients || []).join(', ')}
                      onChange={(e) => updateEmailConfig('recipients', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                      data-testid="email-recipients-input"
                    />
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleTestAlert('email')} data-testid="test-email-btn">
                  <Send className="h-4 w-4 mr-2" /> Send Test Email
                </Button>
              </CardContent>
            )}
          </Card>

          {/* WhatsApp Business Configuration */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-5 w-5 text-green-600" />
                  <div>
                    <CardTitle>WhatsApp Business Notifications</CardTitle>
                    <CardDescription>Configure WhatsApp Business API via Twilio for real-time alerts</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={config?.whatsapp?.enabled || false}
                    onCheckedChange={(checked) => updateWhatsAppConfig('enabled', checked)}
                    data-testid="whatsapp-enabled-switch"
                  />
                  <span className="text-sm">{config?.whatsapp?.enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
            </CardHeader>
            {config?.whatsapp?.enabled && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Twilio Account SID</Label>
                    <Input
                      placeholder="ACxxxxxxxxxxxxxxxxx"
                      value={config?.whatsapp?.account_sid || ''}
                      onChange={(e) => updateWhatsAppConfig('account_sid', e.target.value)}
                      data-testid="twilio-sid-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Twilio Auth Token</Label>
                    <Input
                      type="password"
                      placeholder="Auth token"
                      value={config?.whatsapp?.auth_token || ''}
                      onChange={(e) => updateWhatsAppConfig('auth_token', e.target.value)}
                      data-testid="twilio-token-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>From Number (with country code)</Label>
                    <Input
                      placeholder="whatsapp:+14155238886"
                      value={config?.whatsapp?.from_number || ''}
                      onChange={(e) => updateWhatsAppConfig('from_number', e.target.value)}
                      data-testid="whatsapp-from-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Recipients (comma-separated, with +91)</Label>
                    <Input
                      placeholder="+919876543210, +919876543211"
                      value={(config?.whatsapp?.recipients || []).join(', ')}
                      onChange={(e) => updateWhatsAppConfig('recipients', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                      data-testid="whatsapp-recipients-input"
                    />
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleTestAlert('whatsapp')} data-testid="test-whatsapp-btn">
                  <Send className="h-4 w-4 mr-2" /> Send Test WhatsApp
                </Button>
              </CardContent>
            )}
          </Card>
        </TabsContent>

        {/* ============= ALERT RULES TAB ============= */}
        <TabsContent value="rules" className="space-y-6 mt-4">
          {Object.entries(groupedAlerts).map(([category, alerts]) => (
            <Card key={category}>
              <CardHeader>
                <CardTitle className="capitalize">{CATEGORY_LABELS[category] || category} Alerts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {alerts.map(alert => (
                  <div key={alert.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`alert-rule-${alert.id}`}>
                    <div className="flex items-center gap-3 flex-1">
                      {SEVERITY_ICONS[alert.severity]}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{alert.name}</span>
                          <Badge variant="outline" className={`text-xs ${SEVERITY_COLORS[alert.severity]}`}>
                            {alert.severity}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{alert.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Select
                        value={config?.alert_rules?.[alert.id]?.channels?.[0] || 'email'}
                        onValueChange={(val) => toggleAlertRule(alert.id, 'channels', [val])}
                      >
                        <SelectTrigger className="w-32 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="whatsapp">WhatsApp</SelectItem>
                          <SelectItem value="both">Both</SelectItem>
                        </SelectContent>
                      </Select>
                      <Switch
                        checked={config?.alert_rules?.[alert.id]?.enabled ?? alert.default_enabled}
                        onCheckedChange={(checked) => toggleAlertRule(alert.id, 'enabled', checked)}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ============= SMART RECOMMENDATIONS TAB ============= */}
        <TabsContent value="smart" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-amber-500" /> Smart Alert Recommendations
                  </CardTitle>
                  <CardDescription>AI-analyzed thresholds based on your historical data patterns</CardDescription>
                </div>
                {recommendations.length > 0 && (
                  <Button size="sm" onClick={applyAllRecommendations} data-testid="apply-all-recs-btn">
                    <Zap className="h-4 w-4 mr-1" /> Apply All
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {recsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                  <span className="ml-3 text-slate-500">Analyzing your data...</span>
                </div>
              ) : recommendations.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <Sparkles className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                  <p>No recommendations available. Upload more data to enable smart threshold suggestions.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recommendations.map((rec, idx) => (
                    <div
                      key={rec.alert_type}
                      className="p-4 rounded-xl border border-slate-200 hover:border-amber-300 bg-gradient-to-r from-white to-amber-50/30 dark:from-slate-800 dark:to-amber-900/10 transition-all"
                      data-testid={`rec-${rec.alert_type}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs capitalize">{rec.category}</Badge>
                            <span className="text-sm font-semibold text-slate-800 dark:text-white">{rec.name}</span>
                            <Badge className={rec.confidence === 'high' ? 'bg-green-100 text-green-700 text-xs' : 'bg-yellow-100 text-yellow-700 text-xs'}>
                              {rec.confidence} confidence
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{rec.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span>{rec.data_points?.toLocaleString()} data points</span>
                            {rec.avg_daily_sales && <span>Avg daily: ₹{rec.avg_daily_sales?.toLocaleString()}</span>}
                            {rec.avg_daily_footfall && <span>Avg footfall: {rec.avg_daily_footfall?.toLocaleString()}</span>}
                            {rec.avg_margin && <span>Avg margin: {rec.avg_margin}%</span>}
                            {rec.stores_analyzed && <span>{rec.stores_analyzed} stores</span>}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 min-w-[140px]">
                          <div className="text-right">
                            <p className="text-2xl font-bold text-amber-600">{rec.suggested_threshold}<span className="text-sm font-normal text-slate-500">{rec.unit}</span></p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => applyRecommendation(rec)}
                            data-testid={`apply-rec-${rec.alert_type}`}
                          >
                            <ArrowRight className="h-3 w-3 mr-1" /> Apply
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============= SCHEDULE TAB ============= */}
        <TabsContent value="schedule" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5" /> Delivery Schedule</CardTitle>
              <CardDescription>Configure when and how alert digests are delivered</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Digest Frequency</Label>
                  <Select
                    value={config?.schedule?.digest_frequency || 'daily'}
                    onValueChange={(val) => updateSchedule('digest_frequency', val)}
                  >
                    <SelectTrigger data-testid="digest-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="realtime">Real-time (each alert)</SelectItem>
                      <SelectItem value="hourly">Hourly Digest</SelectItem>
                      <SelectItem value="daily">Daily Digest</SelectItem>
                      <SelectItem value="weekly">Weekly Digest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Digest Time (IST)</Label>
                  <Input
                    type="time"
                    value={config?.schedule?.digest_time || '09:00'}
                    onChange={(e) => updateSchedule('digest_time', e.target.value)}
                    data-testid="digest-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select
                    value={config?.schedule?.timezone || 'Asia/Kolkata'}
                    onValueChange={(val) => updateSchedule('timezone', val)}
                  >
                    <SelectTrigger data-testid="timezone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Asia/Kolkata">IST (Asia/Kolkata)</SelectItem>
                      <SelectItem value="Asia/Dubai">GST (Asia/Dubai)</SelectItem>
                      <SelectItem value="UTC">UTC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <Switch
                  checked={config?.schedule?.critical_immediate ?? true}
                  onCheckedChange={(checked) => updateSchedule('critical_immediate', checked)}
                  data-testid="critical-immediate-switch"
                />
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">Immediate Critical Alerts</p>
                  <p className="text-xs text-red-600 dark:text-red-400">Send critical severity alerts immediately, regardless of digest schedule</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============= HISTORY TAB ============= */}
        <TabsContent value="history" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Alert History</CardTitle>
              <CardDescription>Recent alert deliveries and their status</CardDescription>
            </CardHeader>
            <CardContent>
              {alertHistory.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Bell className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                  <p>No alerts sent yet. Configure channels and enable alert rules to get started.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {alertHistory.map((alert, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg border">
                      <div className="flex items-center gap-3">
                        {alert.channel === 'email' ? <Mail className="h-4 w-4 text-blue-600" /> : <MessageSquare className="h-4 w-4 text-green-600" />}
                        <div>
                          <p className="text-sm font-medium">{alert.alert_type === 'test' ? 'Test Alert' : alert.alert_type}</p>
                          <p className="text-xs text-slate-500">{alert.message?.substring(0, 80)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={alert.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {alert.status === 'sent' ? <CheckCircle2 className="h-3 w-3 mr-1" /> : null}
                          {alert.status}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {alert.created_at ? new Date(alert.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : ''}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AlertSettings;
